import { useState, useEffect, useCallback } from 'react';
import { DollarSign, RefreshCw, TrendingUp } from 'lucide-react';
import { estimateDesignCosts, formatPrice, HOURS_PER_MONTH } from '../../lib/designer/costEstimate';

function CostPanel({ nodes }) {
  const [estimates, setEstimates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await estimateDesignCosts(nodes);
      setEstimates(result);
    } catch (e) {
      setError('Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  }, [nodes]);

  useEffect(() => {
    fetchCosts();
  }, [fetchCosts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">Fetching live prices…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3">
        <p className="text-xs text-red-500 dark:text-red-400 mb-2">{error}</p>
        <button onClick={fetchCosts} className="text-xs text-teal-500 hover:underline">Retry</button>
      </div>
    );
  }

  if (!estimates || estimates.items.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
        <div className="rounded-full bg-gray-100 dark:bg-gray-800 p-3">
          <DollarSign className="h-5 w-5 text-gray-400" />
        </div>
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No costable resources</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">Add VMs or Managed Disks to see cost estimates</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-3 gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Cost Estimate
        </p>
        <button onClick={fetchCosts} className="text-gray-400 hover:text-teal-500" title="Refresh prices">
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <ul className="space-y-2">
        {estimates.items.map((item) => (
          <li key={item.nodeId} className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">{item.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{item.type} &middot; {item.sku}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{item.region}</p>
              </div>
              <div className="text-right shrink-0">
                {item.monthlyCost !== null ? (
                  <p className="text-sm font-bold text-teal-600 dark:text-teal-400">{formatPrice(item.monthlyCost, item.currency)}/mo</p>
                ) : (
                  <p className="text-xs text-gray-400">Price N/A</p>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-1">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">Estimated Monthly Total</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">Based on {HOURS_PER_MONTH} hrs/month</p>
          </div>
          <p className="text-lg font-bold text-teal-600 dark:text-teal-400">{formatPrice(estimates.totalMonthly)}/mo</p>
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
        <TrendingUp className="h-3 w-3" />
        <span>Live prices from Azure Retail Prices API</span>
      </div>
    </div>
  );
}

export default CostPanel;
